// Petit magasin persistant : une valeur JSON dans localStorage, avec abonnement
// pour React (useSyncExternalStore). Toute lecture ou écriture est protégée :
// un navigateur peut refuser le stockage (navigation privée, réglages).

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export class MemoryStorage implements StorageLike {
  private readonly map = new Map<string, string>();
  getItem(key: string): string | null {
    return this.map.get(key) ?? null;
  }
  setItem(key: string, value: string): void {
    this.map.set(key, value);
  }
  removeItem(key: string): void {
    this.map.delete(key);
  }
}

function defaultStorage(): StorageLike {
  try {
    if (typeof localStorage !== 'undefined') {
      const probe = '__pianohome_probe__';
      localStorage.setItem(probe, '1');
      localStorage.removeItem(probe);
      return localStorage;
    }
  } catch {
    // stockage indisponible : on retombe sur la mémoire
  }
  return new MemoryStorage();
}

export interface StoreOptions<T> {
  /** Transforme une valeur lue (ancienne version ou partielle) en valeur valide. */
  migrate?: (raw: unknown) => T | null;
  storage?: StorageLike;
}

export class Store<T extends object> {
  private value: T;
  private readonly listeners = new Set<() => void>();
  private readonly storage: StorageLike;

  constructor(
    readonly key: string,
    private readonly defaults: T,
    options: StoreOptions<T> = {},
  ) {
    this.storage = options.storage ?? defaultStorage();
    this.value = this.load(options.migrate);
  }

  private load(migrate?: (raw: unknown) => T | null): T {
    try {
      const raw = this.storage.getItem(this.key);
      if (raw === null) return { ...this.defaults };
      const parsed: unknown = JSON.parse(raw);
      if (migrate) {
        const migrated = migrate(parsed);
        if (migrated) return migrated;
        return { ...this.defaults };
      }
      if (parsed && typeof parsed === 'object') return { ...this.defaults, ...(parsed as Partial<T>) };
    } catch {
      // valeur illisible : on repart des valeurs par défaut
    }
    return { ...this.defaults };
  }

  get(): T {
    return this.value;
  }

  set(patch: Partial<T> | ((current: T) => T)): void {
    this.value = typeof patch === 'function' ? patch(this.value) : { ...this.value, ...patch };
    try {
      this.storage.setItem(this.key, JSON.stringify(this.value));
    } catch {
      // stockage plein ou refusé : la valeur reste en mémoire pour la session
    }
    for (const l of this.listeners) l();
  }

  reset(): void {
    this.set(() => ({ ...this.defaults }));
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}
